import { useCallback, useEffect, useState } from "react";
import { fonts, palette } from "./pageStyles";

const RANGES = [
  [7, "7 days"],
  [30, "30 days"],
  [90, "90 days"],
];

const PAGE_NAMES = {
  "/": "Landing page",
  "/login": "Sign in",
  "/signup": "Sign up",
  "/forgot-password": "Forgot password",
  "/reset-password": "Reset password",
  "/app/chart": "Chart",
  "/app/python": "Python",
  "/app/backtest": "Backtest",
  "/scanner": "Scanner",
  "/plugins": "Plugins",
  "/profile": "Account",
  "/admin": "Admin",
};

const s = {
  panel: { background: palette.glass, backdropFilter: "blur(8px)", border: `1px solid ${palette.border}`, borderRadius: 6, padding: 16, display: "flex", flexDirection: "column", gap: 12, minWidth: 0 },
  h2: { margin: 0, fontFamily: fonts.display, fontSize: "1rem", fontWeight: 600 },
  small: { fontSize: "0.75rem", color: palette.mutedText },
  row: { display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" },
  th: { textAlign: "left", padding: "8px 8px", borderBottom: `1px solid ${palette.border}`, color: palette.mutedText, fontWeight: 500, fontSize: "0.72rem", whiteSpace: "nowrap" },
  td: { padding: "8px 8px", borderBottom: `1px solid ${palette.border}`, fontSize: "0.82rem", verticalAlign: "middle" },
  ghost: { padding: "6px 10px", minHeight: 32, borderRadius: 4, border: `1px solid ${palette.border}`, background: "transparent", color: palette.text, fontSize: "0.78rem", cursor: "pointer" },
};

const fmt = (n) => new Intl.NumberFormat().format(Number(n) || 0);
const countryName = (code) => {
  if (!code || code === "ZZ") return "Unknown country";
  try { return new Intl.DisplayNames(undefined, { type: "region" }).of(code) || code; }
  catch { return code; }
};

/** "5 min ago", "3 h ago", "2 days ago". Pure. */
export function ago(iso, now = Date.now()) {
  if (!iso) return "never";
  const t = Date.parse(iso.endsWith("Z") || /[+-]\d\d:\d\d$/.test(iso) ? iso : `${iso}Z`); // server times are UTC
  if (!Number.isFinite(t)) return "-";
  const min = Math.max(0, Math.round((now - t) / 60000));
  if (min < 1) return "just now";
  if (min < 60) return `${min} min ago`;
  const h = Math.round(min / 60);
  if (h < 24) return `${h} h ago`;
  const d = Math.round(h / 24);
  return `${d} day${d === 1 ? "" : "s"} ago`;
}

function Card({ label, value, sub }) {
  return (
    <div style={{ flex: "1 1 170px", minWidth: 150, background: palette.glass, border: `1px solid ${palette.border}`, borderRadius: 6, padding: "12px 14px" }}>
      <div style={{ fontSize: "0.7rem", color: palette.mutedText, textTransform: "uppercase", letterSpacing: "0.05em" }}>{label}</div>
      <div style={{ fontFamily: fonts.display, fontSize: "1.4rem", fontWeight: 600 }}>{value}</div>
      {sub && <div style={{ fontSize: "0.72rem", color: palette.mutedText, marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

/** Visitors per day as bars (one series, one axis). Hover or focus a bar for that day's numbers. */
function DailyChart({ daily }) {
  const [hover, setHover] = useState(null);
  const W = 760;
  const H = 200;
  const pad = { l: 34, r: 8, t: 10, b: 24 };
  const plural = (n, one, many) => (Number(n) === 1 ? one : many);
  const max = Math.max(1, ...daily.map((d) => d.visitors));
  const nice = max <= 5 ? 5 : Math.ceil(max / 5) * 5;
  const iw = W - pad.l - pad.r;
  const ih = H - pad.t - pad.b;
  const slot = iw / daily.length;
  const bw = Math.max(2, Math.min(28, slot - 2));
  const y = (v) => pad.t + ih - (v / nice) * ih;
  const ticks = [0, nice / 2, nice];
  const labelEvery = Math.ceil(daily.length / 8);
  const shown = hover !== null ? daily[hover] : null;

  return (
    <div style={{ position: "relative" }}>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" role="img" aria-label={`Visitors per day, ${daily[0]?.date} to ${daily[daily.length - 1]?.date}`} style={{ display: "block", overflow: "visible" }} onMouseLeave={() => setHover(null)}>
        {ticks.map((t) => (
          <g key={t}>
            <line x1={pad.l} x2={W - pad.r} y1={y(t)} y2={y(t)} stroke={palette.border} strokeWidth="1" />
            <text x={pad.l - 6} y={y(t) + 4} textAnchor="end" fontSize="10" fill={palette.mutedText}>{fmt(t)}</text>
          </g>
        ))}
        {daily.map((d, i) => {
          const x = pad.l + i * slot + (slot - bw) / 2;
          const h = Math.max(0, pad.t + ih - y(d.visitors));
          return (
            <g key={d.date}>
              {/* wide invisible hit area so thin bars are easy to hover */}
              <rect x={pad.l + i * slot} y={pad.t} width={slot} height={ih} fill="transparent" onMouseEnter={() => setHover(i)} />
              {d.visitors > 0 && (
                <path
                  d={`M${x},${pad.t + ih} v${-(h - Math.min(4, h))} q0,${-Math.min(4, h)} ${Math.min(4, bw / 2)},${-Math.min(4, h)} h${bw - 2 * Math.min(4, bw / 2)} q${Math.min(4, bw / 2)},0 ${Math.min(4, bw / 2)},${Math.min(4, h)} v${h - Math.min(4, h)} z`}
                  fill={hover === i ? palette.glow : palette.accent}
                  opacity={hover === null || hover === i ? 1 : 0.55}
                  pointerEvents="none"
                />
              )}
              {i % labelEvery === 0 && (
                <text x={pad.l + i * slot + slot / 2} y={H - 6} textAnchor="middle" fontSize="10" fill={palette.mutedText}>{d.date.slice(5)}</text>
              )}
            </g>
          );
        })}
      </svg>
      {shown && (
        <div
          role="status"
          style={{
            position: "absolute",
            top: 0,
            left: `${Math.min(80, Math.max(0, ((pad.l + hover * slot) / W) * 100))}%`,
            transform: "translateY(-4px)",
            background: "#0b1020",
            border: `1px solid ${palette.border}`,
            borderRadius: 4,
            padding: "6px 9px",
            fontSize: "0.75rem",
            pointerEvents: "none",
            whiteSpace: "nowrap",
          }}
        >
          <div style={{ fontWeight: 600 }}>{shown.date}</div>
          <div>{fmt(shown.visitors)} {plural(shown.visitors, "visitor", "visitors")} · {fmt(shown.views)} {plural(shown.views, "page view", "page views")}</div>
          {shown.signups > 0 && <div>{fmt(shown.signups)} new sign-ups</div>}
        </div>
      )}
    </div>
  );
}

function BarList({ rows, valueLabel }) {
  const max = Math.max(1, ...rows.map((r) => r.value));
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      {rows.map((r) => (
        <div key={r.key} style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) auto", gap: 8, fontSize: "0.8rem" }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={r.title || r.label}>{r.label}</div>
            <div style={{ height: 6, background: palette.field, borderRadius: 3, overflow: "hidden", marginTop: 3 }}>
              <div style={{ width: `${(r.value / max) * 100}%`, height: "100%", background: palette.accent, borderRadius: 3 }} />
            </div>
          </div>
          <span style={{ alignSelf: "end", color: palette.text }}>
            {fmt(r.value)} <span style={s.small}>{r.value === 1 ? valueLabel.replace(/s$/, "") : valueLabel}{r.extra ? ` · ${r.extra}` : ""}</span>
          </span>
        </div>
      ))}
    </div>
  );
}

/** Admin "Traffic" tab: visitors, page views, top pages, sources, devices, active users. */
export default function AdminTraffic({ api, onError }) {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [asTable, setAsTable] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api(`/traffic?days=${days}`);
      setData(await res.json());
    } catch (err) {
      onError(err.message);
    } finally {
      setLoading(false);
    }
  }, [api, days, onError]);

  useEffect(() => {
    load();
  }, [load]);

  if (!data) return <div style={s.panel}><div style={s.small}>{loading ? "Loading traffic..." : "No traffic data."}</div></div>;
  const t = data.totals;
  const u = data.users;
  const devices = Object.entries(data.devices || {}).map(([k, v]) => ({ key: k, label: k === "mobile" ? "Phone / tablet" : "Computer", value: v }));
  const browsers = Object.entries(data.browsers || {}).map(([k, v]) => ({ key: k, label: k, value: v }));
  const countries = (data.countries || []).map((row) => ({ key: row.code, label: countryName(row.code), value: row.visitors, extra: `${fmt(row.views)} views` }));
  const pageCounts = new Map(data.pages.map((page) => [page.path, page]));
  const signInPage = pageCounts.get("/login") || { views: 0, visitors: 0 };
  const pageRows = [...new Set([...Object.keys(PAGE_NAMES), ...pageCounts.keys()])]
    .map((path) => ({ path, name: PAGE_NAMES[path] || path, views: pageCounts.get(path)?.views || 0, visitors: pageCounts.get(path)?.visitors || 0 }))
    .sort((a, b) => b.views - a.views || a.name.localeCompare(b.name));
  const noVisits = t.views === 0;

  return (
    <>
      <div style={{ ...s.row, justifyContent: "space-between" }}>
        <div role="group" aria-label="Time range" style={{ display: "flex", gap: 2, padding: 2, border: `1px solid ${palette.border}`, borderRadius: 4 }}>
          {RANGES.map(([n, label]) => (
            <button
              key={n}
              type="button"
              aria-pressed={days === n}
              onClick={() => setDays(n)}
              style={{ ...s.ghost, border: "none", background: days === n ? palette.selected : "transparent" }}
            >
              {label}
            </button>
          ))}
        </div>
        <span style={s.small}>
          {data.from} to {data.to} (UTC){loading ? " · updating..." : ""} ·{" "}
          <button type="button" style={{ ...s.ghost, padding: "2px 8px", minHeight: 0 }} onClick={load}>Refresh</button>
        </span>
      </div>
      {data.truncated && <div role="status" style={{ ...s.small, padding: "8px 10px", border: `1px solid ${palette.border}`, borderRadius: 4 }}>This report reached its 300,000 page-view limit and shows the newest visits only. Counts may be incomplete for this period.</div>}

      <div style={s.row}>
        <Card label="Visitors" value={fmt(t.visitors)} sub={`${fmt(t.visitors_today)} today`} />
        <Card label="Page views" value={fmt(t.views)} sub={`${fmt(t.views_today)} today`} />
        <Card label="Sign-in page views" value={fmt(signInPage.views)} sub={`${fmt(signInPage.visitors)} visitors opened the page`} />
        <Card label="Pages per visitor" value={fmt(t.average_pages_per_visitor)} sub="average in selected range" />
        <Card label="Signed-in users who visited" value={fmt(t.signed_in_users)} sub={`of ${fmt(u.total)} accounts`} />
        <Card label="New sign-ups" value={fmt(t.signups)} sub={`in the last ${data.days} days`} />
        <Card label="Active users" value={fmt(u.active_7d)} sub={`last 7 days · ${fmt(u.active_24h)} in 24 h · ${fmt(u.active_30d)} in 30 days`} />
      </div>

      <div style={s.panel}>
        <div style={{ ...s.row, justifyContent: "space-between" }}>
          <h2 style={s.h2}>Visitors per day</h2>
          <button type="button" style={s.ghost} onClick={() => setAsTable((v) => !v)} aria-pressed={asTable}>{asTable ? "Show chart" : "Show as table"}</button>
        </div>
        {noVisits && <div style={s.small}>No visits counted yet in this period. Signed-in admin visits are excluded.</div>}
        {asTable ? (
          <div style={{ overflowX: "auto", maxHeight: 320 }}>
            <table style={{ borderCollapse: "collapse", width: "100%" }}>
              <thead>
                <tr>{["Day", "Visitors", "Page views", "Sign-ups"].map((h) => <th key={h} style={s.th}>{h}</th>)}</tr>
              </thead>
              <tbody>
                {[...data.daily].reverse().map((d) => (
                  <tr key={d.date}>
                    <td style={s.td}>{d.date}</td>
                    <td style={s.td}>{fmt(d.visitors)}</td>
                    <td style={s.td}>{fmt(d.views)}</td>
                    <td style={s.td}>{fmt(d.signups)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <DailyChart daily={data.daily} />
        )}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: 16 }}>
        <div style={{ ...s.panel, overflowX: "auto" }}>
          <h2 style={s.h2}>Visitors and page views by page</h2>
          <table style={{ borderCollapse: "collapse", width: "100%", minWidth: 360 }}>
            <thead><tr>{["Page", "Page views", "Unique visitors"].map((heading) => <th key={heading} style={s.th}>{heading}</th>)}</tr></thead>
            <tbody>{pageRows.map((page) => (
              <tr key={page.path}>
                <td style={s.td} title={page.path}>{page.name}</td>
                <td style={s.td}>{fmt(page.views)}</td>
                <td style={s.td}>{fmt(page.visitors)}</td>
              </tr>
            ))}</tbody>
          </table>
          <div style={{ ...s.small, marginTop: 8 }}>Each row counts distinct browser IDs that opened that page during the selected date range. Repeat visits increase page views, not unique visitors.</div>
        </div>
        <div style={s.panel}>
          <h2 style={s.h2}>Where visitors came from</h2>
          {data.referrers.length ? (
            <BarList valueLabel="visitors" rows={data.referrers.map((r) => ({ key: r.site, label: r.site, value: r.visitors }))} />
          ) : (
            <div style={s.small}>No visits from other sites yet. Typed addresses, bookmarks and most apps (WhatsApp, Instagram in-app) show up as direct visits, which are not listed here.</div>
          )}
        </div>
        <div style={s.panel}>
          <h2 style={s.h2}>Devices</h2>
          {devices.length ? <BarList valueLabel="visitors" rows={devices} /> : <div style={s.small}>Nothing yet.</div>}
        </div>
        <div style={s.panel}>
          <h2 style={s.h2}>Visitor locations</h2>
          {countries.length ? <BarList valueLabel="visitors" rows={countries} /> : <div style={s.small}>No country data yet. Location appears only when the hosting edge supplies a country code.</div>}
          <div style={{ ...s.small, marginTop: 5 }}>Approximate country from request headers; may be unknown or inaccurate. No precise location or IP address is stored.</div>
        </div>
        <div style={s.panel}>
          <h2 style={s.h2}>Browsers</h2>
          {browsers.length ? <BarList valueLabel="visitors" rows={browsers} /> : <div style={s.small}>Nothing yet.</div>}
        </div>
      </div>

      <div style={s.panel}>
        <h2 style={s.h2}>Visitor activity in this period</h2>
        <div style={{ overflowX: "auto" }}>
          <table style={{ borderCollapse: "collapse", width: "100%", minWidth: 880 }}>
            <thead><tr>{["Visitor", "Country", "Device / browser", "Pages", "Views", "Last page", "First seen", "Last seen"].map((heading) => <th key={heading} style={s.th}>{heading}</th>)}</tr></thead>
            <tbody>
              {(data.visitor_activity || []).map((row) => (
                <tr key={row.visitor}>
                  <td style={s.td} title={row.signed_in ? "At least one page view was signed in" : "No signed-in page views in this range"}>{row.visitor}{row.signed_in ? " · member" : ""}</td>
                  <td style={s.td}>{countryName(row.country)}</td>
                  <td style={s.td}>{row.device} · {row.browser}</td>
                  <td style={s.td} title={(row.page_paths || []).join(", ")}>{fmt(row.pages)}</td>
                  <td style={s.td}>{fmt(row.views)}</td>
                  <td style={s.td} title={row.last_page}>{PAGE_NAMES[row.last_page] || row.last_page}</td>
                  <td style={s.td} title={row.first_seen || ""}>{ago(row.first_seen)}</td>
                  <td style={s.td} title={row.last_seen || ""}>{ago(row.last_seen)}</td>
                </tr>
              ))}
              {!(data.visitor_activity || []).length && <tr><td style={s.td} colSpan={8}><span style={s.small}>No visitor activity in this period.</span></td></tr>}
            </tbody>
          </table>
        </div>
        <div style={s.small}>Showing the 100 most recently active browsers. A visitor is a random browser ID, not a verified person.</div>
      </div>

      <div style={s.panel}>
        <h2 style={s.h2}>Recently active users</h2>
        <div style={{ overflowX: "auto" }}>
          <table style={{ borderCollapse: "collapse", width: "100%", minWidth: 620 }}>
            <thead>
              <tr>{["Name", "Email", "Last seen", `Page views (${data.days} d)`, "Joined"].map((h) => <th key={h} style={s.th}>{h}</th>)}</tr>
            </thead>
            <tbody>
              {data.recent_users.map((r) => (
                <tr key={r.id}>
                  <td style={s.td}>{r.display_name || "-"}</td>
                  <td style={s.td}>{r.email}</td>
                  <td style={s.td} title={r.last_seen_at || ""}>{ago(r.last_seen_at)}</td>
                  <td style={s.td}>{fmt(r.views)}</td>
                  <td style={s.td}>{(r.created_at || "").slice(0, 10) || "-"}</td>
                </tr>
              ))}
              {!data.recent_users.length && (
                <tr><td style={s.td} colSpan={5}><span style={s.small}>No signed-in visits counted yet.</span></td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      <div style={s.small}>
        Counted on our own server: page, day, a random browser ID in local storage, referring site, device, browser and country code from a hosting edge when available. No IP addresses, exact location or cookies are stored;
        browsers with "Do Not Track" are skipped and signed-in admin visits are excluded; anonymous visits from the admin may count. Kept for 180 days. A visitor = one browser, so the same person on phone and
        computer counts twice.
      </div>
      <div style={s.panel}>
        <h2 style={s.h2}>Account access tracking</h2>
        <div style={s.row}>
          {[
            ["/login", "Sign-in page"],
            ["/signup", "Sign-up page"],
            ["/forgot-password", "Forgot password"],
            ["/reset-password", "Reset password"],
          ].map(([path, label]) => {
            const page = pageCounts.get(path) || { views: 0, visitors: 0 };
            return <Card key={path} label={label} value={fmt(page.views)} sub={`${fmt(page.visitors)} visitors opened the page`} />;
          })}
          <Card label="Completed sign-ups" value={fmt(t.signups)} sub={`in the last ${data.days} days`} />
          <Card label="Signed-in users who visited" value={fmt(t.signed_in_users)} sub="in the selected period" />
        </div>
        <div style={s.small}>Page opens show interest in each account flow; they do not prove a sign-in or password reset was completed. Completed registrations and signed-in visits are counted separately. Passwords, reset tokens and credentials are never recorded here.</div>
      </div>
    </>
  );
}
